import { Search, Star, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ReviewSearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  ratingFilter: number | null;
  onRatingFilterChange: (rating: number | null) => void;
  sortBy: 'recent' | 'highest' | 'lowest';
  onSortChange: (sort: 'recent' | 'highest' | 'lowest') => void;
}

export function ReviewSearchBar({
  searchQuery,
  onSearchChange,
  ratingFilter,
  onRatingFilterChange,
  sortBy,
  onSortChange,
}: ReviewSearchBarProps) {
  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search reviews by title, author, or content..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSearchChange('')}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div className="flex gap-2">
        <Select
          value={ratingFilter?.toString() || 'all'}
          onValueChange={(value) => 
            onRatingFilterChange(value === 'all' ? null : parseFloat(value))
          }
        >
          <SelectTrigger className="flex-1 min-w-0">
            <SelectValue placeholder="All Ratings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ratings</SelectItem>
            <SelectItem value="5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 5 Stars
              </div>
            </SelectItem>
            <SelectItem value="4.5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 4.5 Stars
              </div>
            </SelectItem>
            <SelectItem value="4">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 4 Stars
              </div>
            </SelectItem>
            <SelectItem value="3.5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 3.5 Stars
              </div>
            </SelectItem>
            <SelectItem value="3">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 3 Stars
              </div>
            </SelectItem>
            <SelectItem value="2.5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 2.5 Stars
              </div>
            </SelectItem>
            <SelectItem value="2">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 2 Stars
              </div>
            </SelectItem>
            <SelectItem value="1.5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 1.5 Stars
              </div>
            </SelectItem>
            <SelectItem value="1">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 1 Star
              </div>
            </SelectItem>
            <SelectItem value="0.5">
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> 0.5 Stars
              </div>
            </SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger className="flex-1 min-w-0">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most Recent</SelectItem>
            <SelectItem value="highest">Highest Rated</SelectItem>
            <SelectItem value="lowest">Lowest Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
